import classNames from 'classnames';
import get from 'lodash/get';
import some from 'lodash/some';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { DivTooltip, Menu } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { TYPE_GLOBAL_SEARCH } from '~/constants';
import { excludedRealms, isGlobalSearchEnabled } from '~/helpers/ladder';
import { t } from '~/helpers/localization';

import { TooltipBody } from '~/UIKit/components';

import styles from './RealmDropdown.scss';

interface RealmDropdownProps {
  currentSeasonId?: number;
  clanHomeRealm?: string;
  clanBattleRealm?: string;
  itemSelected: string;
  isBrawl: boolean;
  onRealmChange: (realm: string) => void;
}

const RealmDropdown = ({
  currentSeasonId,
  clanHomeRealm,
  clanBattleRealm,
  itemSelected,
  isBrawl,
  onRealmChange,
}: RealmDropdownProps) => {
  const availableRealms = useMemo<string[]>(() => {
    const availableRealmsForSeason: string[] = get(settings, `ladder.seasons.${currentSeasonId}.realms`, []);

    return availableRealmsForSeason.filter((realm) => !excludedRealms.includes(realm));
  }, [currentSeasonId]);

  const isSelectionAllowed = useCallback(
    (realm: string) => {
      if (realm === TYPE_GLOBAL_SEARCH) {
        return isGlobalSearchEnabled && availableRealms.length > 1;
      }

      return availableRealms.includes(realm);
    },
    [availableRealms],
  );

  const getItemContent = useCallback(
    (realm: string, tooltip = true) => {
      const isGlobalRegion = realm === TYPE_GLOBAL_SEARCH;
      const isHomeRegion = realm === clanHomeRealm;
      const isBattleRegion = realm === clanBattleRealm && isBrawl;
      const isHomeNBattle = isHomeRegion && isBattleRegion;

      const name = isGlobalRegion ? t('Глобальный рейтинг') : settings.realmsMap[realm].name;

      const iconStyles: Record<string, boolean> = {};
      iconStyles[styles['icon-globe']] = isGlobalRegion;
      iconStyles[styles['icon-home']] = isHomeRegion;
      iconStyles[styles['icon-fight']] = isBattleRegion;
      iconStyles[styles['icon-home-fight']] = isHomeNBattle;
      iconStyles[styles['has-icon']] = some(Object.keys(iconStyles), (k) => iconStyles[k]);

      const className = classNames(styles['icon-wrapper'], iconStyles);
      const innerContent = <span className={className}>{t(`${name}`)}</span>;

      let tooltipContent = '';

      if (isHomeNBattle) {
        tooltipContent = t('Домашний и боевой регион вашего клана');
      } else if (isHomeRegion) {
        tooltipContent = t('Домашний регион вашего клана');
      } else if (isBattleRegion) {
        tooltipContent = t('Боевой регион вашего клана');
      }

      if (tooltip && tooltipContent !== '') {
        return <DivTooltip tooltipBody={<TooltipBody>{tooltipContent}</TooltipBody>}>{innerContent}</DivTooltip>;
      }

      return innerContent;
    },
    [clanBattleRealm, clanHomeRealm, isBrawl],
  );

  const items = useMemo<Array<{ value: string; content: React.ReactNode }>>(() => {
    const realmsList: Array<{ value: string; content: React.ReactNode }> = [];

    Object.keys(settings.realmsMap).forEach((realm) => {
      if (!availableRealms.includes(realm)) {
        return;
      }

      realmsList.push({
        value: realm,
        content: getItemContent(realm),
      });
    });

    if (isGlobalSearchEnabled && realmsList.length > 1) {
      realmsList.unshift({
        value: TYPE_GLOBAL_SEARCH,
        content: getItemContent(TYPE_GLOBAL_SEARCH),
      });
    }

    return realmsList;
  }, [availableRealms, getItemContent]);

  useEffect(() => {
    if (isSelectionAllowed(itemSelected)) {
      return;
    }

    const recommendedRealm =
      isGlobalSearchEnabled && availableRealms.length > 1 ? TYPE_GLOBAL_SEARCH : availableRealms[0];

    if (recommendedRealm && recommendedRealm !== itemSelected) {
      onRealmChange(recommendedRealm);
    }
  }, [availableRealms, isSelectionAllowed, itemSelected, onRealmChange]);

  if (items.length <= 1) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Menu items={items} itemSelected={itemSelected} onItemChange={onRealmChange}>
        <div className={styles.label}>{getItemContent(itemSelected, false)}</div>
      </Menu>
    </div>
  );
};

export default memo(RealmDropdown);
